import { useMemo } from 'react';
import { Optional } from 'ts-toolbelt/out/Object/Optional';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { useThreadHeaderContext } from '@frontend/thread-header-context';
import { ContactsDrawerBase, type DrawerContact } from '../contacts-drawer-base';
import { ThreadDrawerProps } from '../thread-drawer';

type AssociatedContactsDrawerProps = Optional<
  Omit<ThreadDrawerProps, 'children' | 'closeLabel'>,
  'onClose' | 'open'
> & {
  onSelect: (personId: string) => void;
  onChangePrimaryContact?: ({
    personId,
    firstName,
    lastName,
    preferredName,
  }: {
    personId: string;
    firstName: string;
    lastName: string;
    preferredName: string;
  }) => void;
};

export const AssociatedContactsDrawer = ({
  onSelect,
  onChangePrimaryContact,
  onClose,
  open,
  ...rest
}: AssociatedContactsDrawerProps) => {
  const { t } = useTranslation('thread-header');
  const {
    groupId,
    personPhone,
    primaryContactId,
    associatedContacts,
    associatedContactsDrawerIsOpen,
    setAssociatedContactsDrawerIsOpen,
  } = useThreadHeaderContext();
  const { mutate } = PersonsV3.PersonMutations.useUpsertPrimaryContactLegacyMutation({
    onSuccess: (_, req) => {
      const person = associatedContacts?.find((p) => p.personId === req.personId);
      if (!person) return;
      onChangePrimaryContact?.({
        personId: person.personId,
        firstName: person.firstName ?? '',
        lastName: person.lastName ?? '',
        preferredName: person.preferredName ?? '',
      });
    },
  });
  const contacts: DrawerContact[] = (associatedContacts || []).map((person) => ({
    personId: person.personId,
    person,
    isPrimary: person.personId === primaryContactId,
  }));

  const resolvedOpen = useMemo(() => open || associatedContactsDrawerIsOpen, [open, associatedContactsDrawerIsOpen]);
  const handleClose = () => {
    setAssociatedContactsDrawerIsOpen(false);
    onClose?.();
  };

  return (
    <ContactsDrawerBase
      contacts={contacts}
      title={t('Contacts Under This Number')}
      subtitle={t('Select to focus another contact...')}
      count={associatedContacts?.length ?? 1}
      open={resolvedOpen}
      onClose={handleClose}
      onSelect={onSelect}
      onSetPrimaryContact={(personId) => {
        mutate({ personId, locationId: groupId, phoneNumber: personPhone });
      }}
      locationId={groupId}
      {...rest}
    />
  );
};
